import type { ActionButtonAppearance, ActionButtonProps } from './ActionButton';
import type { ButtonAppearanceProps, ButtonSelectionProps, ButtonSize } from '@natter-co/design-system';

/**
 * Maps the legacy `ActionButton` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary, exactly like `Buttons/Button/toDsProps` and `Buttons/IconButton/
 * toDsIconProps`. It exists so the refreshed control bar (Figma 18187:17601) is gated inside the
 * wrapper rather than at every live-room call site; retiring the flag deletes it.
 *
 * Returning `null` means "DS 2.0 cannot express this button" and sends the caller back to MUI.
 */
type DsProps = ButtonAppearanceProps &
  ButtonSelectionProps & {
    size: ButtonSize;
    loading?: boolean;
    /** A labelled pill (Start Nattering, End Event) rather than a bare icon circle. */
    labelled: boolean;
  };

/**
 * `ActionButton` is 48 / 32; DS buttons are 32 / 36 / 40 / 44.
 *
 * `medium` is mapped to the 44px cell even though the heights do NOT match, which is a deliberate
 * departure from the exact-match rule the other two mappers follow. Those refuse an inexact match
 * because a silent 4-12px growth reflows whatever sits beside the button. Here the 4px shrink IS the
 * design: the control bar in Figma is drawn at 44 throughout, and the DS scale has no 48 cell, so
 * refusing would leave the one surface this exists for on MUI.
 */
const SIZE: Record<NonNullable<ActionButtonProps['size']>, ButtonSize> = {
  medium: 'large',
  small: 'xs',
};

/**
 * The wrapper on the Button page maps Secondary / Default at rest to Primary / Muted selected for
 * accent toggles, or Destructive / Muted selected for muted devices. The action bar's existing
 * appearance names stay here while its refreshed implementation points at those exact cells.
 */
const CELL: Record<ActionButtonAppearance, ButtonAppearanceProps & ButtonSelectionProps> = {
  primary: { appearance: 'primary', fill: 'default' },
  'primary-muted': { appearance: 'primary', fill: 'muted' },
  'primary-selected': { appearance: 'primary', fill: 'muted', isSelected: true },
  secondary: { appearance: 'secondary', fill: 'default' },
  destructive: { appearance: 'destructive', fill: 'default' },
  'destructive-selected': { appearance: 'destructive', fill: 'muted', isSelected: true },
};

/**
 * `sx` is an Emotion prop and does nothing to a Tailwind button, so accepting one silently drops the
 * styling. Seven live-room call sites paint themselves that way — the red mic glyph, the blue active
 * panel, the `width: auto` label pills — and they are exactly the buttons the refresh is for.
 *
 * `dsAppearance` is how a call site resolves that: it states the DS cell that REPLACES its `sx`.
 * Declaring it is what makes the drop deliberate rather than silent. An `sx` with no declaration
 * still falls back to MUI, which is what keeps the call sites outside the live room (Natalie's
 * `LeaveButton`) exactly as they are.
 */
export const toDsActionProps = ({
  children,
  dsAppearance,
  isLoading,
  size = 'medium',
  sx,
}: Pick<ActionButtonProps, 'children' | 'dsAppearance' | 'isLoading' | 'size' | 'sx'>): DsProps | null => {
  if (sx && !dsAppearance) {
    return null;
  }

  return {
    ...CELL[dsAppearance ?? 'secondary'],
    ...(dsAppearance === 'secondary' ? { isSelected: false } : {}),
    size: SIZE[size],
    loading: isLoading,
    // Every labelled call site passes `t(…)`; every icon-only one passes an element. Checking the
    // children rather than `startIcon` also catches the nattering room's host exit, which is a bare
    // text label with no icon beside it.
    labelled: typeof children === 'string',
  };
};
